import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import {
    Select
} from 'final-form-material-ui';
import axios from 'axios';
import {
    useTranslate,
    useNotify,
    Confirm,
    useRedirect,
    Link
} from 'react-admin';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { ThemeProvider } from '@material-ui/styles';
import { createTheme, makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import SecurityIcon from '@material-ui/icons/Security';
import TextField from '@material-ui/core/TextField';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import AttachFileIcon from '@material-ui/icons/AttachFile';

import { lightTheme } from '../layout/themes';
import URL from '../URL';

const useStyles = makeStyles(theme => ({
    main: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: 'url(/img/fondo1.jpg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
    },
    card: {
        minWidth: 200,
        maxWidth: 800,
        marginTop: '3em',
    },
    avatar: {
        margin: '1em',
        display: 'flex',
        justifyContent: 'center',
    },
    icon: {
        backgroundColor: theme.palette.secondary.main,
    },
    hint: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.grey[500],
        padding: '10px'
    },
    form: {
        padding: '0 1em 1em 1em',
    },
    input: {
        marginTop: '1em',
    },
    select: {
        marginTop: '1em',
        width: '100%'
    },
    actions: {
        padding: '0 1em 1em 1em',
    },
    link: {
        textDecoration: 'none',
        color: 'inherit',
    },
    return: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'right',
        color: theme.palette.grey[500],
        padding: '10px'
    },
    fileInput: {
        display: 'none',
    },
    fileList: {
        marginTop: '0.5em',
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}));

const renderInput = ({
    meta: { touched, error } = {},
    input: { ...inputProps },
    ...props
}) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />
);

const ComplaintCreate = ({permissions, ...props}) => {
    const [loading, setLoading] = useState(false);
    const translate = useTranslate();
    const classes = useStyles();
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [id, setId] = useState();
    const redirect = useRedirect();
    const [selectedDate, setSelectedDate] = React.useState(new Date());
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    const categories = [
        { id: 'gestion', name: translate("resources.complaints.categories.gestion") },
        { id: 'robo', name: translate("resources.complaints.categories.robo") },
        { id: 'acoso', name: translate("resources.complaints.categories.acoso") },
        { id: 'trato', name: translate("resources.complaints.categories.trato") },
        { id: 'informacion', name: translate("resources.complaints.categories.informacion") },
        { id: 'fraude', name: translate("resources.complaints.categories.fraude") },
        { id: 'other', name: translate("resources.complaints.categories.other") },
    ];

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length) {
            setSelectedFiles(prev => [...prev, ...files]);
        }
        event.target.value = '';
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const uploadFiles = (complaintId) => {
        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('files', file);
        });
        formData.append('ref', 'complaint');
        formData.append('refId', complaintId);
        formData.append('field', 'images');

        return axios.post(`${URL}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    };

    const validateComplaint = values => {
        const errors = {};     
        return errors;
    };

    const onComplaintCreated = (complaintId) => {
        setLoading(false);
        notify(
            `resources.complaints.notification.created_success`,
            'info',
            {},
            false
        );
        setId(complaintId);
        setOpen(true);
    };

    const sendComplaint = data => {
        setLoading(true);

        axios.post(`${URL}/complaints`, {
              description: data.description,
              category: data.category,
              involved: data.involved,
              date: selectedDate
          })
          .then(({ data: newComplaint }) => {
              if (selectedFiles.length) {
                  notify('pos.uploading', 'info', {}, false);
                  uploadFiles(newComplaint.id)
                      .then(() => onComplaintCreated(newComplaint.id))
                      .catch(() => {
                          setLoading(false);
                          notify('pos.upload_failed', 'warning');
                          setId(newComplaint.id);
                          setOpen(true);
                      });
              } else {
                  onComplaintCreated(newComplaint.id);
              }
          })
          .catch(error => {
              setLoading(false);
              notify(
                  typeof error === 'string'
                      ? error
                      : typeof error === 'undefined' || !error.message
                      ? 'resources.complaints.notification.created_error'
                      : error.message,
                  'warning'
              );
          });
    };
    
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        setOpen(false);
        redirect(`/complaint/${id}`)
    };

    return (
    <Form
        onSubmit={sendComplaint}
        validate={validateComplaint}
        render={({ handleSubmit: sendComplaint }) => (
            <form onSubmit={sendComplaint} noValidate>
                <div className={classes.main}>
                    <Card className={classes.card}>
                        <Link
                            to="/complaint-find"
                            className={classes.link}
                            key={id}
                        >
                            <div className={classes.avatar}>
                                <Avatar className={classes.icon}>
                                    <SecurityIcon />
                                </Avatar>
                            </div>
                        </Link>
                        <div className={classes.hint}>
                            {translate('resources.complaints.public.title')}
                        </div>
                        <div className={classes.form}>
                            <div className={classes.input}>
                                <Field
                                    name="category"
                                    label={translate('resources.complaints.fields.category')}
                                    formControlProps={{className: classes.select}}
                                    component={Select}
                                >
                                {
                                    categories.map(c => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))
                                }
                                </Field>
                            </div>
                            <div className={classes.input}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <KeyboardDatePicker
                                        format="MM/dd/yyyy"
                                        margin="normal"
                                        id="date-picker-inline"
                                        label="Cuándo sucedió"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                            </div>
                            <div className={classes.input}>
                                <Field
                                    name="description"
                                    component={renderInput}
                                    label={translate('resources.complaints.fields.description')}
                                    disabled={loading}
                                    multiline
                                    minRows={10}
                                    maxRows={10}
                                />
                            </div>
                            <div className={classes.input}>
                                <Field
                                    name="involved"
                                    component={renderInput}
                                    label={translate('resources.complaints.fields.involved')}
                                    disabled={loading}
                                    multiline
                                    minRows={6}
                                    maxRows={6}
                                />
                            </div>
                            <div className={classes.input}>
                                <input
                                    ref={fileInputRef}
                                    className={classes.fileInput}
                                    type="file"
                                    multiple
                                    accept="application/pdf, image/*"
                                    onChange={handleFileChange}
                                />
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<AttachFileIcon />}
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    disabled={loading}
                                    fullWidth
                                >
                                    {translate('resources.complaints.fields.attachments')}
                                </Button>
                                {selectedFiles.length > 0 && (
                                    <div className={classes.fileList}>
                                        {selectedFiles.map((file, index) => (
                                            <div key={`${file.name}-${index}`} className={classes.fileItem}>
                                                <Typography variant="body2" noWrap>
                                                    {file.name}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveFile(index)}
                                                    disabled={loading}
                                                    aria-label="remove file"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        ))}
                                        <Typography variant="caption" color="textSecondary">
                                            {translate('pos.upload_remove_help')}
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                        <CardActions className={classes.actions}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={loading}
                                className={classes.button}
                                fullWidth
                            >
                                {translate('pos.submit')}
                            </Button>
                            <Confirm
                                isOpen={open}
                                title={translate('resources.complaints.public.copy')}
                                content={`ID: ${id}`}
                                onConfirm={handleConfirm}
                                onClose={handleDialogClose}
                            />
                        </CardActions>
                        <div className={classes.return}>
                            <Link
                                to="/complaint-find"
                                className={classes.link}
                                key={id}
                            >
                                {translate('pos.return')}
                            </Link>
                        </div>
                    </Card>
                </div>
            </form>
        )}
    />)
};

ComplaintCreate.propTypes = {
    authProvider: PropTypes.func,
    previousRoute: PropTypes.string,
};

// We need to put the ThemeProvider decoration in another component
// Because otherwise the useStyles() hook used in ResetPassword won't get
// the right theme
const ComplaintCreateWithTheme = props => (
    <ThemeProvider theme={createTheme(lightTheme)}>
        <ComplaintCreate {...props} />
    </ThemeProvider>
);

export default ComplaintCreateWithTheme;
